import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';
import AuthGuard from 'utils/route-guard/AuthGuard';
import Marquee from 'react-fast-marquee';
// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { AppBar, Box, CssBaseline, Toolbar, useMediaQuery, Grid, Link } from '@mui/material';
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import Header from './Header';
import Sidebar from './Sidebar';
import { getMenuItems } from 'menu-items';
import { drawerWidth } from 'store/constant';
import { SET_MENU } from 'store/actions';
import { useHeaderVisibility } from '../../hooks/useHeaderVisibility';

// assets
import { IconChevronRight, IconVolume } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { API } from 'utils/api';
import { Alert, Tour } from 'antd';
import { getPerfXCloudLastActive, getUserInfo, setLocalUserInfo } from 'utils/common';
import useNotice from 'hooks/useNotice';

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open, leftDrawerOpened, siderBarWidth }) => ({
  ...theme.typography.mainContent,
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  transition: theme.transitions.create(
    'margin',
    open
      ? {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen
        }
      : {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen
        }
  ),
  transition: 'width 0.2s linear',
  [theme.breakpoints.up('md')]: {
    marginLeft: leftDrawerOpened ? (open ? 0 : -(siderBarWidth - 20)) : '0px',
    width: `calc(100% - ${siderBarWidth}px)`
  },
  [theme.breakpoints.down('md')]: {
    marginLeft: leftDrawerOpened ? '20px' : '0px',
    width: `calc(100% - ${siderBarWidth}px)`,
    padding: '16px'
  },
  [theme.breakpoints.down('sm')]: {
    marginLeft: leftDrawerOpened ? '10px' : '0px',
    width: `calc(100% - ${siderBarWidth}px)`,
    padding: '16px',
    marginRight: '10px'
  }
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
  const navigation = getMenuItems();
  const shouldDisplayHeader = useHeaderVisibility();
  const theme = useTheme();
  const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
  // Handle left drawer
  const leftDrawerOpened = useSelector((state) => state.customization.opened);
  const dispatch = useDispatch();
  const handleLeftDrawerToggle = () => {
    dispatch({ type: SET_MENU, opened: !leftDrawerOpened });
  };
  useEffect(() => {
    if (matchDownMd) {
      dispatch({ type: SET_MENU, opened: false });
    }
  }, [matchDownMd]);
  const getUserTeamInfo = async () => {
    const res = await API.get('/api/teams/check_user_status');
    const { success } = res.data;
    if (success) {
      return res.data;
    } else {
      return {};
    }
  };
  const [user, setUser] = useState(null);
  const loadUser = async () => {
    let res = await API.get(`/api/user/self`);
    const { success, data, role } = res.data;
    if (success) {
      const teamInfo = await getUserTeamInfo();
      const updatedUser = { ...data, ...teamInfo, roleInfo: role };
      setLocalUserInfo(updatedUser);
      setUser(updatedUser);
    }
  };
  const logoToHomeRef = useRef(null);
  const docCenterRef = useRef(null);
  const rightsCenterRef = useRef(null);
  const personalCenterRef = useRef(null);
  const siderBarRef = useRef(null);
  const contentViewRef = useRef(null);
  const [open, setOpenTour] = useState(false);
  const steps = [
    {
      title: '前往首页',
      description: '通过点击此处，您将被带到首页。首页展示了应用的主要功能和信息，是您操作的起点。点击此链接可以快速访问应用的核心页面。',
      target: () => logoToHomeRef.current,
      placement: 'bottomRight'
    },
    {
      title: '帮助中心',
      description: '在帮助中心，您可以找到关于如何使用应用的详细文档和常见问题解答。无论遇到什么困难，帮助中心都是您查找答案的最佳地方。',
      target: () => docCenterRef.current
    },
    {
      title: '权益中心',
      description: '这里展示了您当前的账户权益以及可以享受的各项服务。进入权益中心后，您可以查看不同权益等级的功能和专属特权。',
      target: () => rightsCenterRef.current
    },
    {
      title: '个人中心',
      description: '点击此处可进入个人中心，在这里您可以管理您的账户信息，包括个人资料、余额、账单等。个人中心是管理您账户所有设置的地方。',
      target: () => personalCenterRef.current,
      placement: 'bottomLeft'
    },
    {
      title: '菜单栏',
      description: '菜单栏提供了应用中所有主要功能的快捷访问入口。点击菜单项可以快速跳转到各个功能模块，帮助您高效使用应用。',
      target: () => siderBarRef.current,
      placement: 'right'
    },
    {
      title: '内容区',
      description: '内容区展示了当前页面的主要内容，通常包含了您正在浏览的数据或应用操作的结果。内容区是您进行互动和获取信息的地方。',
      target: () => contentViewRef.current,
      placement: 'center'
    }
  ];
  const getUserGuideStatus = async () => {
    const last_active_at = getPerfXCloudLastActive();
    if (!last_active_at) {
      try {
        const { success, data } = (await API.get('/api/userguide')).data;
        if (success) {
          setOpenTour(data == 'false' ? false : true);
          return;
        }
        setOpenTour(true);
      } catch (error) {
        setOpenTour(true);
      }
    }
  };
  useEffect(() => {
    loadUser();
    getUserGuideStatus().then();
  }, []);
  const location = useLocation();
  const siderBarWidth = leftDrawerOpened ? drawerWidth : 0;

  const { notice, noticeType, noticeIcon, noticeHeight, hideNoticeHeight } = useNotice();
  const onCloseAlert = () => {
    hideNoticeHeight();
  };
  return (
    <Box sx={{ display: 'flex', overflowY: 'hidden' }}>
      <Tour open={open} onClose={() => setOpenTour(false)} steps={steps} />
      <CssBaseline />
      {shouldDisplayHeader && (
        <>
          <AppBar
            enableColorOnDark
            position="fixed"
            color="inherit"
            elevation={0}
            sx={{
              bgcolor: theme.palette.background.default,
              transition: leftDrawerOpened ? theme.transitions.create('width') : 'none',
              height: `${56 + noticeHeight}px`,
              //borderBottom:'1px gray solid',
              boxShadow: '0px 1px 8px 0px #0000000F',
              zIndex: 99
            }}
          >
            {!!noticeHeight && (
              <Alert
                type={noticeType}
                closable
                onClose={onCloseAlert}
                showIcon
                icon={noticeIcon}
                banner
                style={{ padding: '0px 10px', color: 'rgba(0, 0, 34, 0.5)', fontSize: '12px' }}
                message={
                  <Marquee pauseOnHover gradient={false}>
                    {notice}
                  </Marquee>
                }
              />
            )}
            <Toolbar sx={{ py: '17px', pl: '20px' }}>
              <Header
                logoToHomeRef={logoToHomeRef}
                rightsCenterRef={rightsCenterRef}
                docCenterRef={docCenterRef}
                personalCenterRef={personalCenterRef}
                handleLeftDrawerToggle={handleLeftDrawerToggle}
              />
            </Toolbar>
          </AppBar>
        </>
      )}
      <Sidebar
        noticeHeight={noticeHeight}
        siderBarRef={siderBarRef}
        user={user}
        drawerOpen={!matchDownMd ? leftDrawerOpened : !leftDrawerOpened}
        drawerToggle={handleLeftDrawerToggle}
      />
      <Main
        ref={contentViewRef}
        leftDrawerOpened={leftDrawerOpened}
        siderBarWidth={siderBarWidth}
        theme={theme}
        open={leftDrawerOpened}
        style={{
          marginTop: shouldDisplayHeader ? noticeHeight + 56 + 'px' : `${80 + noticeHeight}px`,
          minHeight: `calc(100vh - ${56 + noticeHeight}px)`,
          background:
            location.pathname == '/panel/modelsquare'
              ? 'linear-gradient(to bottom, #e6eafe 10%, #ffffff 70%)'
              : location.pathname == '/panel/statistics/index' ||
                location.pathname == '/panel/usage/statistics/index' ||
                location.pathname == '/panel/dashboard/index' ||
                location.pathname == '/panel/index'
              ? '#f5f7ff'
              : 'linear-gradient(#F4F7FF 1%, #FFFFFF 10%)',
          borderRadius: '0px',
          padding:
            location.pathname.includes('datasets') ||
            location.pathname.includes('agent') ||
            location.pathname.includes('exploration_center') ||
            location.pathname.includes('cueWordproject')
              ? '0px'
              : undefined
        }}
      >
        <div
          style={{
            minHeight:
              location.pathname.includes('datasets') ||
              location.pathname.includes('agent') ||
              location.pathname.includes('exploration_center') ||
              location.pathname.includes('cueWordproject')
                ? `calc(100vh - ${56 + noticeHeight}px)`
                : `calc(100vh - ${56 + noticeHeight + 42}px)`,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
          <AuthGuard>
            <Grid flex={1} style={{ display: 'flex', flexDirection: 'column' }}>
              <Outlet />
            </Grid>
          </AuthGuard>
        </div>
      </Main>
    </Box>
  );
};

export default MainLayout;
